import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'

import {
  ALL_ACCORDION_BLOCK_ITEMS_QUERY,
  SINGLE_ACCORDION_BLOCK_ITEM_QUERY,
  ACCORDION_BLOCK_ITEMS_DEPENDENTS_QUERY,
  CREATE_ACCORDION_BLOCK_ITEM_MUTATION,
  UPDATE_ACCORDION_BLOCK_ITEM_MUTATION,
  DELETE_ACCORDION_BLOCK_ITEM_MUTATION,
  FORCE_DELETE_ACCORDION_BLOCK_ITEM_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsAccordionBlockItemRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ACCORDION_BLOCK_ITEM_QUERY, variables).then((data: any) => {
      return hydrateObject(data.accordionBlockItem)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ACCORDION_BLOCK_ITEMS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.accordionBlockItems)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ACCORDION_BLOCK_ITEM_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.accordionBlockItem),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ACCORDION_BLOCK_ITEMS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.accordionBlockItems),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ACCORDION_BLOCK_ITEMS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ACCORDION_BLOCK_ITEMS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ACCORDION_BLOCK_ITEM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accordionBlockItem)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ACCORDION_BLOCK_ITEM_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accordionBlockItem)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ACCORDION_BLOCK_ITEM_MUTATION, variables).then((data: any) => {
      return data.deleteCmsAccordionBlockItem
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ACCORDION_BLOCK_ITEM_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsAccordionBlockItem
    })
  },
}

export default cmsAccordionBlockItemRepository
